import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  VNotificationCategoryEnum,
  VNotificationStatusEnum,
  VNotificationTypeEnum,
  VTank,
} from '../../services';
import { useAppDispatch, useAppSelector } from '../../hook';
import {
  changeEndDate,
  changeStartDate,
  fetchNotifications,
  fetchReadings,
  fetchTankAndClinic,
  updateNotification,
  muteTank,
  unmuteTank,
  changeRightBarType,
  unsnoozeTank,
} from './_slice';
import TankStats from './features/tankStats';
import TankSettings from './features/tankSettings';
import NotificationList from './features/notificationList';
import ReadingsChart from './features/readingsChart';
import Layout3 from '../../layouts/layout3';
import TankTopBar from './features/tankTopBar';
import Breadcrumbs from '../../components/breadcrumbs';
import appConfig from '../../config.app';
import { EnumRightBarType } from '../../common/models';
import SnoozeDialog from './features/snoozeDialog';
import AlarmList from './features/alarmList';
import { getAlarmCount } from '../../common/helper';

interface RouteParams {
  tankId: string;
  [key: string]: string;
}

const TankDashboard: React.FC = () => {
  const dashboardState = useAppSelector((state) => state.dashboard);
  const dispatch = useAppDispatch();
  const { tankId } = useParams<RouteParams>();
  const [alarmToSnooze, setAlarmToSnooze] = React.useState<{
    tank: VTank;
    notificationType: VNotificationTypeEnum;
  }>();

  useEffect(() => {
    dispatch(fetchTankAndClinic({ tankId, needLoading: true }));
    dispatch(fetchReadings({ tankId }));
    dispatch(fetchNotifications({ tankId }));

    const intervalId = setInterval(() => {
      dispatch(fetchTankAndClinic({ tankId }));
      dispatch(fetchReadings({ tankId }));
      dispatch(fetchNotifications({ tankId }));
    }, appConfig.pollIntervalSeconds * 1000);

    return () => clearInterval(intervalId);
  }, [dispatch, tankId]);

  const getBreadcrumbs = () => {
    const breadcrumbs = [{ label: 'Clinics', path: '/clinics' }];
    if (dashboardState.clinic) {
      breadcrumbs.push({
        label: dashboardState.clinic?.name,
        path: `/clinic-dashboard/${dashboardState.clinic?.id}`,
      });
    }
    return breadcrumbs;
  };

  return (
    <Layout3
      breadcrumbsSlot={
        <Breadcrumbs
          breadcrumbs={getBreadcrumbs()}
          title={dashboardState.tank ? dashboardState.tank.name : ''}
        />
      }
      contentTopSlot={
        <TankStats
          clinic={dashboardState.clinic}
          tank={dashboardState.tank}
          caddy={dashboardState.caddy}
          status={dashboardState.tankLoadingStatus}
          onMute={() => dispatch(muteTank())}
          onUnmute={() => dispatch(unmuteTank())}
        />
      }
      contentSlot={
        <ReadingsChart
          tank={dashboardState.tank}
          readings={dashboardState.readings}
          startDate={dashboardState.startDateFilter}
          endDate={dashboardState.endDateFilter}
          onChangeStartDate={(newDate) => dispatch(changeStartDate(newDate))}
          onChangeEndDate={(newDate) => dispatch(changeEndDate(newDate))}
          onFitlerReadings={
            () => dispatch(
              fetchReadings({ tankId }),
            )
          }
        />
      }
      topSlot={
        <TankTopBar
          tank={ dashboardState.tank }
          rightBarType={dashboardState.rightBarType}
          alarmCount={dashboardState.tank ? getAlarmCount([dashboardState.tank]) : 0}
          onChangeRightBarType={(x) => dispatch(changeRightBarType(x))}
        />
      }
      rightSlot1Hide={ dashboardState.rightBarType !== EnumRightBarType.Alarms }
      rightSlot1={
        <AlarmList
          style={{}}
          tanks={dashboardState.tank ? [dashboardState.tank] : []}
          onSnooze={(tank, notificationType) => setAlarmToSnooze({ tank, notificationType })}
          onUnsnooze={
            (_tankId, notificationType) => {
              dispatch(unsnoozeTank({ tankId: _tankId, notificationType }));
            }
          }
        />
      }
      rightSlot2Hide={ dashboardState.rightBarType !== EnumRightBarType.History }
      rightSlot2={
        <NotificationList
          onDismiss={
            (notificationId) => dispatch(
              updateNotification({
                notificationId,
                status: VNotificationStatusEnum.Dismissed,
              }),
            )
          }
          onSwitchStatus={
            (notificationId, status) => dispatch(
              updateNotification({
                notificationId,
                status,
              }),
            )
          }
          style={{}}
          notifications={dashboardState.notifications}
        />
      }
      rightSlot3Hide={ dashboardState.rightBarType !== EnumRightBarType.Settings }
      rightSlot3={
        <TankSettings
          clinic={ dashboardState.clinic }
          tank={ dashboardState.tank }
          caddy={ dashboardState.caddy }
        />
      }
      dialogSlotShow={alarmToSnooze !== undefined}
      dialogSlot={
        alarmToSnooze && <SnoozeDialog
          tankId={alarmToSnooze.tank.id}
          tankName={alarmToSnooze.tank.name}
          notificationCategory={VNotificationCategoryEnum.Alarming}
          notificationType={alarmToSnooze.notificationType}
          onCancel={() => setAlarmToSnooze(undefined)}
          onConfirm={() => setAlarmToSnooze(undefined)}
        />
      }
    />
  );
};

export default TankDashboard;
